import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import useSocialMeta from "../hooks/useSocialMeta";
import "./TheAsk.css";

const PHOTO = "/brand/rasaan-hakiem-photo.jpg";

const TIERS = [
  { label: "Founding Friend", amount: "$25+" },
  { label: "Founding Supporter", amount: "$100+" },
  { label: "Founding Partner", amount: "$250+" },
  { label: "Founding Patron", amount: "$500+" },
  { label: "Founding Benefactor", amount: "$1,000+" },
];

const TRUST = [
  "Secure Stripe Checkout",
  "Contributions support Version 1.0 development",
  "This is not an investment or equity offering",
  "Every contribution helps launch Liturgical Intelligence™",
];

export default function TheAsk() {
  useSocialMeta({
    title: "Help Me Launch the Future of Church Worship | Liturgical Intelligence™",
    description: "A personal letter from Rasaan Hakiem about building the future of worship planning and why I'm asking friends to help launch Version 1.0.",
    path: "/the-ask",
  });
  useEffect(() => {
    window.posthog?.capture?.("the_ask_viewed");
    const cta = document.querySelector('[data-testid="ta-final-cta"]');
    if (!cta) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { window.posthog?.capture?.("letter_completed"); io.disconnect(); }
      });
    }, { threshold: 0.4 });
    io.observe(cta);
    return () => io.disconnect();
  }, []);
  const track = (source) => window.posthog?.capture?.("founding_circle_click", { source });
  return (
    <div className="ta-page" data-testid="the-ask-page">
      <nav className="ta-nav">
        <div className="ta-wrap ta-nav-inner">
          <Link to="/" className="ta-brand" data-testid="ta-brand">
            Liturgical<span>Intelligence</span>™
          </Link>
          <div className="ta-nav-links">
            <Link to="/" data-testid="ta-nav-home">Home</Link>
            <Link to="/sundaybuilder" data-testid="ta-nav-sundaybuilder">SundayBuilder™</Link>
            <Link to="/founding-circle" data-testid="ta-nav-founding">Founding Circle</Link>
            <Link to="/login" data-testid="ta-nav-login">Member Login</Link>
          </div>
        </div>
      </nav>

      <header className="ta-hero">
        <div className="ta-wrap">
          <div className="ta-eyebrow">A Personal Letter</div>
          <h1 data-testid="ta-hero-title">Help Me Launch the Future of Church Worship</h1>
          <p className="ta-hero-brand">Liturgical Intelligence™</p>
          <p className="ta-hero-sub">A personal letter from Rasaan Hakiem.</p>
          <p className="ta-readtime" data-testid="ta-readtime">3 minute read</p>
        </div>
      </header>

      <div className="ta-wrap ta-photo-wrap">
        <figure className="ta-photo" data-testid="ta-photo">
          <img src={PHOTO} alt="Rasaan Hakiem — Founder of Liturgical Intelligence™" />
          <figcaption>Rasaan Hakiem · Founder &amp; CEO, Liturgical Intelligence™</figcaption>
        </figure>
      </div>

      <section className="ta-wrap ta-cred" data-testid="ta-cred">
        <div className="ta-cred-lead">20+ Years Serving Churches</div>
        <div className="ta-cred-roles">
          <span>Director of Music</span>
          <span className="ta-cred-sep">·</span>
          <span>Organist</span>
          <span className="ta-cred-sep">·</span>
          <span>Founder of Liturgical Intelligence™</span>
        </div>
        <div className="ta-cred-denoms">Serving Episcopal, Catholic, Lutheran &amp; Methodist Churches</div>
      </section>

      <section className="ta-wrap ta-intro" data-testid="ta-intro">
        <p>
          If you're reading this, it's because you've been part of my journey in some way. I wanted to
          personally share what I've been building—and why I'm asking for your help today.
        </p>
      </section>

      <article className="ta-wrap ta-letter" data-testid="ta-letter">
        <h2 className="ta-letter-head">A Personal Letter</h2>
        <p className="ta-salutation">Dear Friends,</p>

        <p>
          Over the past several months I've been pouring everything I have into building something I
          truly believe can serve churches in a way that doesn't currently exist.
        </p>
        <p>It's called <strong>Liturgical Intelligence™</strong>.</p>
        <p>
          At its heart, it's an AI-powered platform that helps churches, clergy, musicians, and
          ministry leaders plan worship more intelligently—not by replacing ministry, but by giving
          leaders back time and preserving the institutional wisdom that often disappears when staff
          change.
        </p>
        <p>
          After more than twenty years serving churches as an organist, choir director, and worship
          leader, I've experienced firsthand how much time churches spend rebuilding what they
          already once knew. Every leadership transition risks losing traditions, planning documents,
          repertoire, and institutional knowledge that took years to develop.
        </p>
        <p>Liturgical Intelligence™ was born from that experience.</p>
        <p>
          What began as a simple idea has grown into a working platform. Today it includes secure
          member accounts, SundayBuilder™, Parish Intelligence™, Parish Memory™, long-range worship
          planning, and an expanding suite of applications designed specifically for churches—all
          powered by the Ordo AI™ Intelligence Engine.
        </p>
        <p>Version 1.0 is nearly ready.</p>
        <p>The reality is this:</p>
        <p className="ta-emphasis">I'm incredibly close.</p>
        <p className="ta-emphasis">I'm also out of runway.</p>
        <p>
          To complete Version 1.0, finalize the production infrastructure, secure the Liturgical
          Intelligence™ trademarks, and publicly launch the platform, I need to raise
          <strong> $5,000</strong> over the next few weeks.
        </p>
        <p>I'm not asking everyone to contribute that amount.</p>
        <p>
          I'm simply asking those who have believed in me over the years—and who believe in what I'm
          trying to build—to help me carry this final stretch.
        </p>
        <p>
          Whether your contribution is <strong>$25, $100, $250, $500, $1,000, or any amount you're
          able to give</strong>, every gift moves this project one step closer to serving churches
          around the world.
        </p>
        <p>More than anything, I'm asking for your belief.</p>
        <p>
          I've spent my career serving the Church through sacred music, worship, and liturgy. Building
          Liturgical Intelligence™ feels like the next chapter of that calling. My hope is to create
          technology that strengthens ministry without replacing it—giving pastors, musicians, and
          worship leaders more time to care for people instead of paperwork.
        </p>
        <p>
          As a small expression of gratitude, contributors will be recognized as members of the
          <strong> Liturgical Intelligence™ Founding Circle</strong>.
        </p>

        <h3 className="ta-tiers-head">Founding Circle Recognition</h3>
        <ul className="ta-tiers" data-testid="ta-tiers">
          {TIERS.map((t) => (
            <li key={t.label}>
              <span className="ta-tier-label">{t.label}</span>
              <span className="ta-tier-amt">{t.amount}</span>
            </li>
          ))}
        </ul>

        <p>
          Unless you prefer to remain anonymous, Founding Circle members will be permanently
          recognized on the Liturgical Intelligence™ website as the people who believed in this vision
          before Version 1.0 officially launched.
        </p>
        <p>
          Please know this is <strong>not an investment or ownership opportunity</strong>.
          Contributions are voluntary gifts that directly support the completion and launch of
          Liturgical Intelligence™.
        </p>
        <p>If you're able to contribute, thank you.</p>
        <p>
          If you're unable to give at this time, I completely understand. Sharing this project with
          someone who may believe in the vision would mean just as much.
        </p>
        <p>
          Thank you for believing in me over the years. Your encouragement, friendship, and prayers
          have carried me farther than you probably realize.
        </p>
        <p>
          I hope very soon I'll be able to share a platform that helps churches flourish—and one that
          you're proud to have helped bring into the world.
        </p>

        <p className="ta-signoff">With gratitude,</p>
        <p className="ta-signature">Rasaan Hakiem</p>
        <p className="ta-signature-title">Founder, Liturgical Intelligence™</p>
        <p className="ta-signature-cred">Sacred Musician · Liturgist · 20+ Years Serving Churches Nationwide</p>

        <div className="ta-continue">
          <h3>Thank you for taking the time to read my story.</h3>
          <Link to="/founding-circle" className="ta-btn ta-btn-primary" data-testid="ta-continue-btn" onClick={() => track("continue")}>
            Continue to the Founding Circle <ArrowRight size={18} />
          </Link>
          <p className="ta-secondary">
            Prefer to go directly?{" "}
            <Link to="/founding-circle" data-testid="ta-direct-link" onClick={() => track("direct")}>Go to the Founding Circle</Link>
          </p>
        </div>
      </article>

      <section className="ta-trust-section" data-testid="ta-trust">
        <div className="ta-wrap">
          <ul className="ta-trust-list">
            {TRUST.map((t) => (
              <li key={t}><Check size={16} /> {t}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="ta-final-cta" data-testid="ta-final-cta">
        <div className="ta-wrap">
          <p className="ta-final-lead">
            If you believe this platform can strengthen churches for years to come, I'd be honored to
            have you become one of our Founding Supporters.
          </p>
          <Link to="/founding-circle" className="ta-btn ta-btn-gold" data-testid="ta-final-cta-btn" onClick={() => track("final_cta")}>
            Become a Founding Supporter
          </Link>
          <p className="ta-final-sub">
            Every contribution—large or small—moves Version 1.0 one step closer to launch.
          </p>
        </div>
      </section>

      <footer className="ta-footer">
        <div className="ta-wrap">
          <span>© Rasaan Hakiem LLC</span>
          <span className="ta-dot">·</span>
          <span>Liturgical<b>Intelligence</b>™</span>
          <span className="ta-dot">·</span>
          <span>Powered by Ordo AI™</span>
        </div>
      </footer>
    </div>
  );
}
